import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  formatHeurePlanning,
  groupPlanningByDay,
  vacationHoraireLabel,
  type PublicPlanningSession,
} from '../../utils/publicPlanningDisplay';

type Props = {
  sessions: PublicPlanningSession[];
  loading?: boolean;
  joursPlanning?: string | null;
  seancesParSemaine?: number;
};

export default function PublicFilierePlanningTrustSection({
  sessions,
  loading,
  joursPlanning,
  seancesParSemaine = 0,
}: Props) {
  const byDay = groupPlanningByDay(sessions);
  const firstSession = byDay[0]?.sessions[0];

  return (
    <section className="pfd-planning-trust bg-pattern-circles" data-purpose="trust-building-hero">
      <div className="pfd-planning-trust__copy" data-purpose="text-content">
        <h2 className="pfd-planning-trust__title">
          Planning de votre
          <br />
          formation
        </h2>
        <p className="pfd-planning-trust__desc">
          Créneaux hebdomadaires, vacations et salles associés à cette formation.
          {joursPlanning ? ` Jours concernés : ${joursPlanning}.` : ''}
          {seancesParSemaine > 0
            ? ` ${seancesParSemaine} séance${seancesParSemaine > 1 ? 's' : ''} par semaine.`
            : ''}
        </p>
        <div className="pfd-planning-trust__actions">
          <a href="#details-formation" className="pfd-planning-trust__btn pfd-planning-trust__btn--primary">
            Voir les détails
          </a>
        </div>
      </div>

      <div className="pfd-planning-trust__visual" data-purpose="floating-ui-container">
        {loading ? (
          <div className="pfd-planning-trust__card pfd-planning-trust__card--empty">
            <p className="pfd-planning-trust__card-empty-text">Chargement des créneaux…</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="pfd-planning-trust__card pfd-planning-trust__card--empty">
            <LucideSymbolIcon name="calendar_today" tone="muted" size={28} className="mx-auto opacity-60" />
            <p className="pfd-planning-trust__card-empty-title">Planning en préparation</p>
            <p className="pfd-planning-trust__card-empty-text">
              Les vacations et salles seront affichées dès validation du calendrier officiel.
            </p>
          </div>
        ) : (
          <>
            <div className="pfd-planning-trust__card">
              <div className="pfd-planning-trust__card-stack">
                <div className="pfd-planning-trust__days">
                  {byDay.map((day) => {
                    const session = day.sessions[0];
                    if (!session) return null;
                    return (
                      <div key={day.jour} className="pfd-planning-trust__row">
                        <div className="pfd-planning-trust__row-left">
                          <span className="pfd-planning-trust__row-icon" aria-hidden>
                            <LucideSymbolIcon name="calendar_month" tone="inherit" size={16} />
                          </span>
                          <div className="pfd-planning-trust__row-text">
                            <span className="pfd-planning-trust__row-label">{day.label}</span>
                            <span className="pfd-planning-trust__row-meta">
                              {vacationHoraireLabel(session)}
                            </span>
                          </div>
                        </div>
                        <div className="pfd-planning-trust__row-right">
                          <div className="pfd-planning-trust__row-value">
                            {session.salle?.libelle ?? session.salle?.code ?? 'Salle'}
                          </div>
                          {day.sessions.length > 1 ? (
                            <div className="pfd-planning-trust__row-meta">
                              +{day.sessions.length - 1} créneau
                              {day.sessions.length - 1 > 1 ? 'x' : ''}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="pfd-planning-trust__card-note">
                  Consultez le détail complet dans la section{' '}
                  <a className="pfd-planning-trust__card-link" href="#details-formation">
                    cette formation
                  </a>
                  .
                </p>

                <div className="pfd-planning-trust__card-actions">
                  <a href="#contenu-formation" className="pfd-planning-trust__chip pfd-planning-trust__chip--slate-light">
                    Contenu
                  </a>
                  <a href="#tarifs" className="pfd-planning-trust__chip pfd-planning-trust__chip--slate">
                    Tarifs
                  </a>
                </div>
              </div>
            </div>

            {firstSession ? (
              <div className="pfd-planning-trust__bubble">
                <span className="pfd-planning-trust__bubble-icon" aria-hidden>
                  <LucideSymbolIcon name="schedule" tone="primary" size={20} />
                </span>
                <div>
                  <div className="pfd-planning-trust__bubble-label">À quelle heure ?</div>
                  <div className="pfd-planning-trust__bubble-value">
                    {firstSession.vacation?.heureDebut && firstSession.vacation?.heureFin
                      ? `De ${formatHeurePlanning(firstSession.vacation.heureDebut)} à ${formatHeurePlanning(firstSession.vacation.heureFin)}`
                      : vacationHoraireLabel(firstSession)}
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
